import {
  VentureProjectTypeCreateDocument,
  VentureProjectTypeDeleteDocument,
  VentureProjectTypeGetByIdDocument,
  VentureProjectTypeListDocument,
  VentureProjectTypeUpdateDocument,
  VentureProjectTypesPaginatedDocument,
} from "api/generated";
import { RequestTypes } from "api/services/enums";
import { getRequestInstance } from "api/services/utils";
import { PartialNullable } from "config";

import { client } from "config/apollo";
import { executeEviction } from "api/utils/cache-eviction";
import {
  IVentureProjectTypeCreateVars,
  IVentureProjectTypeListVars,
  IVentureProjectTypeModel,
  IVentureProjectTypePaginatedResponse,
  IVentureProjectTypePaginatedVars,
  IVentureProjectTypeUpdateVars,
  VentureProjectTypeCreateUpdateResponse,
  VentureProjectTypeListResponse,
} from "./types";

export async function getVentureProjectTypesPaginated(
  variables: IVentureProjectTypePaginatedVars
): Promise<IVentureProjectTypePaginatedResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureProjectTypesPaginatedDocument,
      variables,
      fetchPolicy: "network-only",
    });

    return res?.data?.ventureProjectTypesPaginated;
  } catch (error) {
    throw error;
  }
}

export async function createVentureProjectType(
  variables: IVentureProjectTypeCreateVars
): Promise<VentureProjectTypeCreateUpdateResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: VentureProjectTypeCreateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureProjectType",
      fieldNames: ["ventureProjectTypesPaginated", "ventureProjectTypeList"],
    });

    return res?.data?.ventureProjectTypeCreate;
  } catch (error) {
    throw error;
  }
}

export async function updateVentureProjectType(
  variables: IVentureProjectTypeUpdateVars
): Promise<VentureProjectTypeCreateUpdateResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: VentureProjectTypeUpdateDocument,
      variables,
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureProjectType",
      id: variables.ventureProjectTypeId.toString(),
      fieldNames: [
        "ventureProjectTypesPaginated",
        "ventureProjectTypeGetById",
        "ventureProjectTypeList",
      ],
    });

    return res?.data?.ventureProjectTypeUpdate;
  } catch (error) {
    throw error;
  }
}

export async function getVentureProjectType(
  ventureProjectTypeId: number
): Promise<PartialNullable<IVentureProjectTypeModel>> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureProjectTypeGetByIdDocument,
      variables: {
        ventureProjectTypeId,
      },
      fetchPolicy: "network-only",
    });

    return res?.data?.ventureProjectTypeGetById;
  } catch (error) {
    throw error;
  }
}

export async function deleteVentureProjectType(
  ventureProjectTypeId: number
): Promise<boolean> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.MUTATE);
    const res = await makeRequest({
      mutation: VentureProjectTypeDeleteDocument,
      variables: {
        ventureProjectTypeId,
      },
      fetchPolicy: "no-cache",
    });

    executeEviction(client, {
      typename: "VentureProjectType",
      id: ventureProjectTypeId.toString(),
      fieldNames: ["ventureProjectTypesPaginated", "ventureProjectTypeList"],
    });

    return Boolean(res?.data?.ventureProjectTypeDelete);
  } catch (error) {
    throw error;
  }
}

export async function getVentureProjectTypes(
  variables: IVentureProjectTypeListVars
): Promise<VentureProjectTypeListResponse> {
  try {
    const makeRequest = getRequestInstance(RequestTypes.QUERY);
    const res = await makeRequest({
      query: VentureProjectTypeListDocument,
      variables,
      fetchPolicy: "cache-first",
    });

    return res?.data?.ventureProjectTypeList;
  } catch (error) {
    throw error;
  }
}
